import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faSearch, 
  faTimes, 
  faPlus,
  faTrash,
  faBuilding,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { fetchAuthSession } from 'aws-amplify/auth';
import './styles/PartnerEmployeeModal.css';

const PartnerEmployeeModal = ({ isOpen, onClose, partners, onAssignmentsUpdated }) => {
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [matchingEmployees, setMatchingEmployees] = useState([]);
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [partnerAssignments, setPartnerAssignments] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load all partner-employee assignments on modal open
  useEffect(() => {
    if (isOpen) {
      console.log('Modal opened with partners:', partners);
      loadPartnerAssignments();
    }
  }, [isOpen]);

  // Load assigned employees when partner is selected
  useEffect(() => {
    if (selectedPartnerId) {
      const assignments = partnerAssignments[selectedPartnerId] || [];
      setAssignedEmployees(assignments);
    } else {
      setAssignedEmployees([]);
    }
  }, [selectedPartnerId, partnerAssignments]);

  // Search employees when search term changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (employeeSearch.length >= 2 && selectedPartnerId) {
        searchEmployees(employeeSearch);
      } else {
        setMatchingEmployees([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [employeeSearch, selectedPartnerId]);

  const loadPartnerAssignments = async () => {
    try {
      const { tokens } = await fetchAuthSession();
      
      // Load assignments for all partners
      const assignments = {};
      
      for (const partner of partners) {
        try {
          const partnerId = partner.partner_id || partner.id;
          const response = await axios.get(`https://track.snapped.cc/api/leads/partners/${partnerId}/employees`, {
            headers: {
              'Authorization': `Bearer ${tokens.accessToken.toString()}`
            }
          });
          assignments[partnerId] = response.data || [];
        } catch (error) {
          console.error(`Error loading assignments for partner ${partnerId}:`, error);
          assignments[partnerId] = [];
        }
      }
      
      setPartnerAssignments(assignments);
    } catch (error) {
      console.error('Error loading partner assignments:', error);
      toast.error('Failed to load partner assignments');
    }
  };

  const searchEmployees = async (searchTerm) => {
    if (!selectedPartnerId) return;
    
    setLoading(true);
    try {
      const { tokens } = await fetchAuthSession();
      
      console.log('Searching employees with:', {
        endpoint: API_ENDPOINTS.EMPLOYEES.SEARCH,
        partner_id: selectedPartnerId,
        search: searchTerm,
        hasToken: !!tokens.accessToken
      });
      
      // Use the exact same pattern as EditModal
      const response = await axios.get(API_ENDPOINTS.EMPLOYEES.SEARCH, {
        params: {
          partner_id: selectedPartnerId,
          search: searchTerm
        },
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      
      console.log('Employee search response:', response.data);
      setMatchingEmployees(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error searching employees:', error);
      // Don't show detailed error to user, just like EditModal
    } finally {
      setLoading(false);
    }
  };

  const handleAssignEmployee = async (employee) => {
    if (!selectedPartnerId) return;
    
    try {
      setSaving(true);
      const { tokens } = await fetchAuthSession();
      
      // Save assignment to our partner-employee system
      await axios.post(`https://track.snapped.cc/api/leads/partners/${selectedPartnerId}/employees`, {
        user_id: employee.user_id,
        first_name: employee.first_name,
        last_name: employee.last_name,
        email: employee.email
      }, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      
      // Update local state
      const updatedAssignments = {
        ...partnerAssignments,
        [selectedPartnerId]: [...assignedEmployees, employee]
      };
      setPartnerAssignments(updatedAssignments);
      
      // Clear search
      setEmployeeSearch('');
      setMatchingEmployees([]);
      
      toast.success(`Successfully assigned ${employee.first_name} ${employee.last_name} to partner`);
      
      // Notify parent component of changes
      if (onAssignmentsUpdated) {
        onAssignmentsUpdated(updatedAssignments);
      }
    } catch (error) {
      console.error('Error assigning employee:', error);
      toast.error('Failed to assign employee');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveEmployee = async (userId) => {
    if (!selectedPartnerId) return;
    
    try {
      setSaving(true);
      const { tokens } = await fetchAuthSession();
      
      // Remove assignment from backend
      await axios.delete(`https://track.snapped.cc/api/leads/partners/${selectedPartnerId}/employees/${userId}`, {
        headers: {
          'Authorization': `Bearer ${tokens.accessToken.toString()}`
        }
      });
      
      // Update local state
      const updatedEmployees = assignedEmployees.filter(emp => emp.user_id !== userId);
      const updatedAssignments = {
        ...partnerAssignments,
        [selectedPartnerId]: updatedEmployees
      };
      setPartnerAssignments(updatedAssignments);
      
      toast.success('Successfully removed employee assignment');
      
      // Notify parent component of changes
      if (onAssignmentsUpdated) {
        onAssignmentsUpdated(updatedAssignments);
      }
    } catch (error) {
      console.error('Error removing employee:', error);
      toast.error('Failed to remove employee assignment');
    } finally {
      setSaving(false);
    }
  };

  const selectedPartner = partners.find(p => p.id === selectedPartnerId);

  if (!isOpen) return null;

  return (
    <div className="partner-employee-modal-overlay" onClick={onClose}>
      <div className="partner-employee-modal" onClick={e => e.stopPropagation()}>
        <div className="partner-employee-modal-header">
          <div className="modal-title">
            <h2>Partner Employee Assignments</h2>
          </div>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="partner-employee-modal-content">
          {/* Partner Selection */}
          <div className="partner-selection-section">
            <label>
              <FontAwesomeIcon icon={faBuilding} />
              Select Partner Company
            </label>
            <select
              value={selectedPartnerId}
              onChange={(e) => {
                console.log('Partner selected:', e.target.value);
                setSelectedPartnerId(e.target.value);
                setEmployeeSearch('');
                setMatchingEmployees([]);
              }}
              className="partner-select"
            >
              <option value="">Select a partner...</option>
              {partners.map(partner => (
                <option key={partner.id} value={partner.partner_id || partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>

          {selectedPartnerId && (
            <>
              {/* Employee Search */}
              <div className="employee-search-section">
                <label>
                  <FontAwesomeIcon icon={faSearch} />
                  Search Employees for {selectedPartner?.name}
                </label>
                <div className="search-input-container">
                  <input
                    type="text"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    placeholder="Type employee name or email..."
                    className="employee-search-input"
                  />
                  {employeeSearch.length >= 2 && matchingEmployees.length > 0 && (
                    <div className="pm-search-results">
                      {matchingEmployees.map(emp => (
                        <div 
                          key={emp.user_id}
                          className="pm-search-result"
                          onClick={() => handleAssignEmployee(emp)}
                        >
                          {emp.first_name} {emp.last_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Assigned Employees */}
              <div className="assigned-employees-section">
                <label>
                  <FontAwesomeIcon icon={faUsers} />
                  Assigned Employees ({assignedEmployees.length})
                </label>
                
                {assignedEmployees.length > 0 ? (
                  <div className="assigned-employees-list">
                    {assignedEmployees.map(employee => (
                      <div key={employee.user_id} className="assigned-employee-item">
                        <div className="employee-info">
                          <FontAwesomeIcon icon={faUser} />
                          <span className="employee-name">
                            {employee.first_name} {employee.last_name}
                          </span>
                          <span className="employee-email">{employee.email}</span>
                          <span className="employee-id">ID: {employee.user_id}</span>
                        </div>
                        <button
                          onClick={() => handleRemoveEmployee(employee.user_id)}
                          className="remove-button"
                          disabled={saving}
                          title="Remove assignment"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-employees-message">
                    No employees assigned to this partner yet.
                    <br />
                    <small>Search and assign employees above.</small>
                  </div>
                )}
              </div>
            </>
          )}

          {!selectedPartnerId && (
            <div className="no-partner-selected">
              <FontAwesomeIcon icon={faBuilding} />
              <p>Select a partner company above to view and manage employee assignments.</p>
            </div>
          )}
        </div>

        <div className="partner-employee-modal-footer">
          <button onClick={onClose} className="close-modal-button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartnerEmployeeModal;
